import React, { useState } from 'react';
import { Box } from '@mui/material';
import { AppHeader } from '../../../../components/app-header';
import { useMetaTitle } from '../../../../hooks';
import { CitizenAttendanceProtocol } from './components/citizen-attendance-protocol';
import { RestrictedCitizenAttendanceProtocol } from './components/restricted-citizen-attendance-protocol';
import { CircularProgressCentered } from '../../../../components/circular-progress-centered';
import { AttendanceProtocolTitlezone } from './components/attendance-protocol-titlezone';
import { AttendanceProtocolActionBar } from './components/attendance-protocol-action-bar';
import { AttendanceProtocolFab } from './components/attendance-protocol-fab';
import { AreaAside, AreaContent } from '../../../../components/area-layout';
import { useAttendanceProtocol } from './hooks/use-attendance-protocol';
import { AttendanceSheetStateDto } from '../../../../api-service/clients';
import { AttendanceProtocolCompletionDialog, ProtocolCompletionAction } from './components/attendance-protocol-completion-dialog';
import { AttendanceProtocolPdfDialog } from './components/attendance-protocol-pdf-dialog';
import { useFeatureToggles } from '../../../../feature-toggles/feature-toggles';
import { useAttendanceRights } from '../../use-attendance-rights';
import { PDF_OPEN_AND_DOWNLOAD_TOGGLE } from '../../../../components/pdf-actions/use-pdf-actions';

export function AttendanceProtocol() {
  useMetaTitle('Protokol');

  const { isFeatureEnabled } = useFeatureToggles();
  const { canEditProtocol } = useAttendanceRights();

  const {
    state,
    isLoading,
    isSaving,
    isGeneratingPdf,
    handleAttendanceStartChange,
    handleAttendanceEndChange,
    handleAttendancesStartChange,
    handleAttendancesEndChange,
    handleNotRelevantChange,
    handleNotRelevantsChange,
    handleSaveRegistrationDetails,
    handleSubmit,
    handleApprove,
    handleRegisterNoShows,
    handleCreatePdf,
    handleOpenPdf,
    handleDownloadPdf,
  } = useAttendanceProtocol();
  const pdfActions = isFeatureEnabled(PDF_OPEN_AND_DOWNLOAD_TOGGLE) ? { onOpen: handleOpenPdf, onDownload: handleDownloadPdf } : undefined;

  const [pendingCompletion, setPendingCompletion] = useState<ProtocolCompletionAction>();
  const [isPdfDialogOpen, setIsPdfDialogOpen] = useState(false);

  const {
    citizenLessonAttendances,
    courseName,
    lessonBeginAt,
    lessonEndAt,
    showStartAttendanceRegistration,
    showEndAttendanceRegistration,
    attendanceSheetState,
    requiresApproval,
    restrictedCitizenCount,
  } = state;

  const hasAttendanceChecks = showStartAttendanceRegistration || showEndAttendanceRegistration;
  const allChecksComplete = citizenLessonAttendances.every(
    x =>
      (!showStartAttendanceRegistration || x.requiresStartCheck === false || !!x.startRegistration) &&
      (!showEndAttendanceRegistration || x.requiresEndCheck === false || !!x.endRegistration)
  );

  const isLocked = attendanceSheetState === AttendanceSheetStateDto.Submitted;
  const lessonHasBegun = new Date(lessonBeginAt) <= new Date();
  const allowApprovalBeforeLessonStart = isFeatureEnabled('EnableAttendanceApprovalBeforeLessonStart');

  function handleRequestSubmit() {
    setPendingCompletion(requiresApproval ? 'submitForApproval' : 'submit');
  }

  function handleRequestApproval() {
    setPendingCompletion('approve');
  }

  function handleCancelCompletion() {
    setPendingCompletion(undefined);
  }

  async function handleConfirmCompletion() {
    if (pendingCompletion === 'approve') {
      await handleApprove();
    } else {
      await handleSubmit();
    }
    setPendingCompletion(undefined);
  }

  return (
    <Box>
      <AppHeader />
      {isLoading ? (
        <CircularProgressCentered />
      ) : (
        <>
          <Box display="flex" flexDirection="column">
            <AttendanceProtocolTitlezone courseName={courseName} lessonBeginAt={lessonBeginAt} lessonEndAt={lessonEndAt} attendanceSheetState={attendanceSheetState} />
            <Box display="flex" justifyContent="center">
              <Box display="flex" flexGrow={1} gap={6} sx={{ maxWidth: 1845 }}>
                <AreaContent>
                  <Box display="flex" flexDirection="column" gap={1}>
                    <AttendanceProtocolActionBar
                      isSaving={isSaving}
                      onAttendancesStartChange={handleAttendancesStartChange}
                      onAttendancesEndChange={handleAttendancesEndChange}
                      onNotRelevantsChange={handleNotRelevantsChange}
                      showStartAttendanceRegistration={showStartAttendanceRegistration}
                      showEndAttendanceRegistration={showEndAttendanceRegistration}
                      citizenLessonAttendances={citizenLessonAttendances}
                      isLocked={isLocked}
                      isReadOnly={!canEditProtocol}
                    />
                    {citizenLessonAttendances.map(citizenLessonAttendance => (
                      <CitizenAttendanceProtocol
                        isSaving={isSaving}
                        showStartAttendanceRegistration={showStartAttendanceRegistration}
                        showEndAttendanceRegistration={showEndAttendanceRegistration}
                        key={citizenLessonAttendance.citizenId}
                        citizenLessonAttendance={citizenLessonAttendance}
                        lessonBeginAt={lessonBeginAt}
                        lessonEndAt={lessonEndAt}
                        onAttendanceStartChange={handleAttendanceStartChange}
                        onAttendanceEndChange={handleAttendanceEndChange}
                        onNotRelevantChange={handleNotRelevantChange}
                        onSaveRegistrationDetails={handleSaveRegistrationDetails}
                        isLocked={isLocked}
                        isReadOnly={!canEditProtocol}
                      />
                    ))}
                    {[...Array(restrictedCitizenCount)].map((_, index) => (
                      <RestrictedCitizenAttendanceProtocol
                        key={index}
                        showStartAttendanceRegistration={showStartAttendanceRegistration}
                        showEndAttendanceRegistration={showEndAttendanceRegistration}
                      />
                    ))}
                  </Box>
                </AreaContent>
                <AreaAside>
                  <AttendanceProtocolFab
                    attendanceSheetState={attendanceSheetState}
                    allChecksComplete={allChecksComplete}
                    hasAttendanceChecks={hasAttendanceChecks}
                    isLocked={isLocked}
                    canEditProtocol={canEditProtocol}
                    requiresApproval={requiresApproval}
                    lessonHasBegun={lessonHasBegun}
                    allowApprovalBeforeLessonStart={allowApprovalBeforeLessonStart}
                    hasRestrictedCitizens={restrictedCitizenCount > 0}
                    isGeneratingPdf={isGeneratingPdf}
                    onRegisterNoShows={handleRegisterNoShows}
                    onSubmit={handleRequestSubmit}
                    onApprove={handleRequestApproval}
                    onCreatePdf={() => setIsPdfDialogOpen(true)}
                  />
                </AreaAside>
              </Box>
            </Box>
          </Box>
          <AttendanceProtocolCompletionDialog action={pendingCompletion} onConfirm={handleConfirmCompletion} onCancel={handleCancelCompletion} />
          <AttendanceProtocolPdfDialog open={isPdfDialogOpen} onClose={() => setIsPdfDialogOpen(false)} onConfirm={handleCreatePdf} pdfActions={pdfActions} />
        </>
      )}
    </Box>
  );
}
